import { useMemo, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import CategoryChips from '../components/CategoryChips';
import ProductGrid from '../components/ProductGrid';
import ResultsToolbar from '../components/ResultsToolbar';
import SearchInput from '../components/SearchInput';
import { categoryIcon } from '../lib/format';
import { useCategories, useProducts } from '../lib/queries';
import { useDebounce } from '../lib/useDebounce';
import type { Category, ProductSort } from '../lib/api';

function flatten(categories: Category[]): Category[] {
  return categories.flatMap((c) => [c, ...flatten(c.children ?? [])]);
}

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<ProductSort>('newest');
  const q = useDebounce(search.trim(), 350);

  const { data: categories } = useCategories();
  const category = useMemo(
    () => flatten(categories ?? []).find((c) => c.slug === slug),
    [categories, slug],
  );

  const query = useProducts({ category: slug, ...(q ? { q } : {}), sort, limit: 24 });
  const Icon = category ? categoryIcon(category.icon) : null;

  return (
    <div className="grid-paper">
      <section className="mx-auto max-w-6xl px-4 py-8 md:py-12">
        <nav
          className="mb-5 flex items-center gap-1.5 overflow-x-auto whitespace-nowrap text-sm text-ink-soft"
          aria-label="Drobečková navigace"
        >
          <Link to="/" className="py-1 hover:text-ink">
            Katalog
          </Link>
          <ChevronRight className="h-4 w-4 flex-none" strokeWidth={2} />
          <span className="font-medium text-ink">{category?.name ?? slug}</span>
        </nav>

        <div className="mb-6 flex items-center gap-3">
          {Icon && <Icon className="h-7 w-7 flex-none text-board" strokeWidth={2} />}
          <div>
            <h1 className="text-[1.7rem] md:text-3xl">{category?.name ?? 'Kategorie'}</h1>
            {category?.description && (
              <p className="text-sm text-ink-soft">{category.description}</p>
            )}
          </div>
        </div>

        <div className="mb-4 max-w-xl">
          <SearchInput value={search} onChange={setSearch} placeholder="Hledat v kategorii…" />
        </div>

        <div className="mb-4">
          <CategoryChips activeSlug={slug} />
        </div>

        <ResultsToolbar
          count={query.data?.total}
          loading={query.isLoading}
          query={q}
          sort={sort}
          onSortChange={setSort}
        />

        <ProductGrid
          data={query.data}
          isLoading={query.isLoading}
          isError={query.isError}
          error={query.error}
          emptyLabel={
            q ? `Nic jsme nenašli pro „${q}"` : 'V této kategorii zatím nejsou žádné materiály'
          }
        />
      </section>
    </div>
  );
}
